import React from 'react';
import {
  Eye,
  Users,
  AlertTriangle,
  CheckCircle2,
  ShoppingBag,
  Calendar,
  ThumbsUp,
  Heart,
  Clock,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Receipt,
  Star,
  Bike,
  UtensilsCrossed,
  Radio,
  Home,
  CreditCard,
  MessageSquare,
  Gift,
  UserCheck,
  Award,
} from 'lucide-react';
import {
  formatMoney,
  formatDate,
  WEEKDAYS_PT,
  normalizeProductName,
  uniqueProductNames,
  pedidoCanalMeta,
  feedbackSentimentTone,
  feedbackSentimentLabel,
  reservationStatusTone,
  formatRelativeDate,
} from '../utils/crmDashboardHelpers';

function EmptyState({ label }) {
  return <p className="crm-empty">{label}</p>;
}

function buildPageNumbers(totalPages, currentPage) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const result = [1];
  const startWindow = Math.max(2, currentPage - 1);
  const endWindow = Math.min(totalPages - 1, currentPage + 1);
  if (startWindow > 2) result.push('…');
  for (let i = startWindow; i <= endWindow; i += 1) result.push(i);
  if (endWindow < totalPages - 1) result.push('…');
  result.push(totalPages);
  return result;
}

const PROFILE_PAGE_SIZE = 10;

export default function CrmProfileTabContent({
  cliente,
  activeTab,
  profilePage,
  setProfilePage,
  onSelectOrder,
  feedbacksAtendidosIds,
  onOpenAddresses,
}) {
const slicePage = (arr) => {
  const total = arr.length;
  const totalPages = Math.max(1, Math.ceil(total / PROFILE_PAGE_SIZE));
  const safe = Math.min(profilePage, totalPages);
  const start = (safe - 1) * PROFILE_PAGE_SIZE;
  return {
    items: arr.slice(start, start + PROFILE_PAGE_SIZE),
    total,
    totalPages,
    safePage: safe,
    pageStart: start,
    pageEnd: start + PROFILE_PAGE_SIZE,
  };
};

const renderProfilePagination = (slice, itemLabel) => {
  if (slice.totalPages <= 1) return null;
  const numbers = buildPageNumbers(slice.totalPages, slice.safePage);
  return (
    <div className="crm-results-info" style={{ marginTop: 12 }}>
      <span>
        Mostrando{' '}
        <strong>
          {slice.pageStart + 1}–{Math.min(slice.pageEnd, slice.total)}
        </strong>{' '}
        de <strong>{slice.total}</strong> {itemLabel}
      </span>
      <nav className="crm-pagination" aria-label="Paginação">
        <button
          type="button"
          onClick={() => setProfilePage((p) => Math.max(1, p - 1))}
          disabled={slice.safePage === 1}
          aria-label="Página anterior"
        >
          <ChevronLeft size={14} />
        </button>
        {numbers.map((p, idx) =>
          p === '…' ? (
            <span key={`ellipsis-${idx}`} className="crm-pagination-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => setProfilePage(p)}
              className={p === slice.safePage ? 'active' : ''}
              aria-current={p === slice.safePage ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => setProfilePage((p) => Math.min(slice.totalPages, p + 1))}
          disabled={slice.safePage === slice.totalPages}
          aria-label="Próxima página"
        >
          <ChevronRight size={14} />
        </button>
      </nav>
    </div>
  );
};

const renderPedidos = () => {
  const pedidos = cliente?.pedidos || [];
  const deliveryCount = pedidos.filter((p) => String(p.type || '').toLowerCase().includes('deliv')).length;
  const salaoCount = pedidos.filter((p) => {
    const t = String(p.type || '').toLowerCase();
    return t.includes('salão') || t.includes('salao');
  }).length;
  const totalValue = pedidos.reduce((sum, p) => sum + Number(p.value || 0), 0);
  const ticket = pedidos.length ? totalValue / pedidos.length : 0;
  const pctDelivery = pedidos.length ? Math.round((deliveryCount / pedidos.length) * 100) : 0;

  let chipTone = null;
  let chipLabel = '';
  let chipIcon = null;
  if (pedidos.length) {
    if (pctDelivery >= 70) {
      chipTone = 'good';
      chipLabel = 'Cliente prefere delivery';
      chipIcon = Bike;
    } else if (pctDelivery <= 30) {
      chipTone = 'good';
      chipLabel = 'Cliente prefere o salão';
      chipIcon = UtensilsCrossed;
    } else {
      chipTone = 'good';
      chipLabel = 'Mistura canais de consumo';
      chipIcon = ShoppingBag;
    }
  }

  return (
    <>
      {chipTone && (
        <div className={`crm-trust-chip ${chipTone}`}>
          {chipIcon && React.createElement(chipIcon, { size: 13 })}
          {chipLabel}
        </div>
      )}

      <div className="crm-kpi-row">
        <div className={`crm-kpi-mini enhanced ${!pedidos.length ? 'empty' : ''}`}
             style={{ '--mini-bg': '#EEF2FF', '--mini-fg': '#4F46E5' }}>
          <div className="crm-kpi-mini-head">
            <span>Total de pedidos</span>
            <div className="crm-kpi-mini-icon"><ShoppingBag size={11} /></div>
          </div>
          <strong>{pedidos.length}</strong>
          <div className="crm-kpi-mini-sub">
            {pedidos.length
              ? `${deliveryCount} delivery · ${salaoCount} salão`
              : 'sem pedidos'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!totalValue ? 'empty' : ''}`}
             style={{ '--mini-bg': '#F0FDFA', '--mini-fg': '#0D9488' }}>
          <div className="crm-kpi-mini-head">
            <span>Receita acumulada</span>
            <div className="crm-kpi-mini-icon"><Wallet size={11} /></div>
          </div>
          <strong>{formatMoney(totalValue)}</strong>
          <div className="crm-kpi-mini-sub">
            {totalValue ? 'soma de todos os pedidos' : 'sem receita registrada'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!ticket ? 'empty' : ''}`}
             style={{ '--mini-bg': '#FFFBEB', '--mini-fg': '#D97706' }}>
          <div className="crm-kpi-mini-head">
            <span>Ticket médio</span>
            <div className="crm-kpi-mini-icon"><Receipt size={11} /></div>
          </div>
          <strong>{formatMoney(ticket)}</strong>
          <div className="crm-kpi-mini-sub">
            {ticket ? 'por pedido' : 'sem base para cálculo'}
          </div>
        </div>
      </div>

      {!pedidos.length ? (
        <EmptyState label="Sem pedidos registrados." />
      ) : (
        (() => {
          const slice = slicePage(pedidos);
          return (
            <>
              <div className="crm-list">
                {slice.items.map((pedido, index) => {
                  const dateObj = pedido.date ? new Date(pedido.date) : null;
                  const validDate = dateObj && !Number.isNaN(dateObj.getTime());
                  const weekday = validDate ? WEEKDAYS_PT[dateObj.getDay()] : '';
                  const dayStr = validDate ? formatDate(pedido.date) : 'Data desconhecida';
                  const titleParts = [weekday, dayStr].filter(Boolean).join(', ');
                  const canal = pedidoCanalMeta(pedido.type);
                  const value = Number(pedido.value || 0);
                  const CanalIcon = canal.icon === 'bike' ? Bike : canal.icon === 'salao' ? UtensilsCrossed : ShoppingBag;

                  return (
                    <div
                      className={`crm-row feedback-row stripe-${canal.tone}`}
                      key={`${pedido.id || slice.pageStart + index}-${pedido.date}`}
                    >
                      <span className="crm-reserva-stripe" aria-hidden="true" />
                      <div className="crm-cell left">
                        <div className="crm-reserva-body">
                          <div className="crm-name">{titleParts}</div>
                          <div className="crm-phone crm-reserva-sub">
                            <span className={`crm-reserva-people-inline ${!value ? 'crm-num-muted' : ''}`}>
                              <Wallet size={11} />
                              {value > 0 ? formatMoney(value) : 'R$ 0'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="crm-pedido-actions">
                        <span className={`crm-badge crm-canal-badge crm-canal-${canal.tone}`}>
                          <CanalIcon size={11} />
                          {canal.label}
                        </span>
                        <button
                          type="button"
                          className="crm-btn small icon-only"
                          onClick={() => onSelectOrder(pedido)}
                          title="Ver detalhes do pedido"
                          aria-label="Ver detalhes do pedido"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderProfilePagination(slice, pedidos.length === 1 ? 'pedido' : 'pedidos')}
            </>
          );
        })()
      )}
    </>
  );
};

const renderVisitas = () => {
  const visitas = cliente?.visitas || [];
  const brindesRetirados = visitas.filter((v) => v.benefit).length;
  const niveisRegistrados = visitas
    .map((v) => Number(v.level || 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  const nivelAtual = niveisRegistrados.length ? Math.max(...niveisRegistrados) : 0;

  // Chip de engajamento adaptativo
  let chipTone = null;
  let chipLabel = '';
  let chipIcon = null;
  if (visitas.length >= 10) {
    chipTone = 'good';
    chipLabel = 'Cliente fiel ao salão';
    chipIcon = Award;
  } else if (visitas.length >= 5) {
    chipTone = 'good';
    chipLabel = 'Visita o salão com frequência';
    chipIcon = UserCheck;
  } else if (visitas.length >= 1) {
    chipTone = 'warn';
    chipLabel = 'Poucas visitas registradas';
    chipIcon = UserCheck;
  }

  return (
    <>
      {chipTone && (
        <div className={`crm-trust-chip ${chipTone}`}>
          {chipIcon && React.createElement(chipIcon, { size: 13 })}
          {chipLabel}
        </div>
      )}

      <div className="crm-kpi-row">
        <div className={`crm-kpi-mini enhanced ${!visitas.length ? 'empty' : ''}`}
             style={{ '--mini-bg': '#EEF2FF', '--mini-fg': '#4F46E5' }}>
          <div className="crm-kpi-mini-head">
            <span>Total de check-ins</span>
            <div className="crm-kpi-mini-icon"><UserCheck size={11} /></div>
          </div>
          <strong>{visitas.length}</strong>
          <div className="crm-kpi-mini-sub">
            {visitas.length ? 'no salão' : 'nenhuma visita'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!brindesRetirados ? 'empty' : ''}`}
             style={{ '--mini-bg': '#F0FDFA', '--mini-fg': '#0D9488' }}>
          <div className="crm-kpi-mini-head">
            <span>Brindes retirados</span>
            <div className="crm-kpi-mini-icon"><Gift size={11} /></div>
          </div>
          <strong>{brindesRetirados}</strong>
          <div className="crm-kpi-mini-sub">
            {brindesRetirados
              ? `${brindesRetirados} de ${visitas.length} visitas`
              : 'ainda não resgatou benefícios'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!nivelAtual ? 'empty' : ''}`}
             style={{ '--mini-bg': '#F5F3FF', '--mini-fg': '#7C3AED' }}>
          <div className="crm-kpi-mini-head">
            <span>Nível atual</span>
            <div className="crm-kpi-mini-icon"><Award size={11} /></div>
          </div>
          <strong>{nivelAtual || '-'}</strong>
          <div className="crm-kpi-mini-sub">
            {nivelAtual ? 'pontuação no clube' : 'sem nível registrado'}
          </div>
        </div>
      </div>

      {!visitas.length ? (
        <EmptyState label="Sem visitas registradas." />
      ) : (
        (() => {
          const slice = slicePage(visitas);
          return (
            <>
              <div className="crm-list">
                {slice.items.map((visita, index) => {
                  const dateObj = visita.date ? new Date(visita.date) : null;
                  const validDate = dateObj && !Number.isNaN(dateObj.getTime());
                  const weekday = validDate ? WEEKDAYS_PT[dateObj.getDay()] : '';
                  const dayStr = validDate ? formatDate(visita.date) : 'Data desconhecida';
                  const titleParts = [weekday, dayStr].filter(Boolean).join(', ');
                  const tone = visita.benefit ? 'good' : '';
                  const nivel = Number(visita.level || 0);

                  return (
                    <div
                      className={`crm-row feedback-row stripe-${tone || 'neutral'}`}
                      key={`${visita.date}-${slice.pageStart + index}`}
                    >
                      <span className="crm-reserva-stripe" aria-hidden="true" />
                      <div className="crm-cell left">
                        <div className="crm-reserva-body">
                          <div className="crm-name">{titleParts}</div>
                          <div className="crm-phone crm-reserva-sub">
                            {visita.benefit ? (
                              <span className="crm-reserva-people-inline" style={{ color: 'var(--crm-good-fg)' }}>
                                <Gift size={11} />
                                Brinde retirado
                              </span>
                            ) : (
                              <span className="crm-reserva-people-inline">
                                <Gift size={11} />
                                Sem brinde
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span className="crm-badge crm-level-badge">
                          <Award size={11} />
                          Nível {nivel || '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderProfilePagination(slice, visitas.length === 1 ? 'visita' : 'visitas')}
            </>
          );
        })()
      )}
    </>
  );
};

const renderFeedback = () => {
  const feedbacks = cliente?.feedbacks || [];
  const negatives = feedbacks.filter((feedback) => feedback.class === 'negativo');
  const positives = feedbacks.filter((feedback) => feedback.class === 'positivo');
  const average = feedbacks.length
    ? (feedbacks.reduce((sum, feedback) => sum + Number(feedback.score || 0), 0) / feedbacks.length)
    : 0;
  const averageDisplay = average ? (Math.round(average * 10) / 10).toString().replace('.', ',') : '-';
  const pctPositivo = feedbacks.length ? Math.round((positives.length / feedbacks.length) * 100) : 0;

  let chipTone = null;
  let chipLabel = '';
  let chipIcon = null;
  if (feedbacks.length) {
    if (negatives.length > 0 && pctPositivo < 70) {
      chipTone = 'warn';
      chipLabel = 'Atenção: há feedback negativo recente';
      chipIcon = AlertTriangle;
    } else if (pctPositivo >= 80) {
      chipTone = 'good';
      chipLabel = `${pctPositivo}% de avaliações positivas`;
      chipIcon = CheckCircle2;
    } else {
      chipTone = 'good';
      chipLabel = 'Satisfação em acompanhamento';
      chipIcon = ThumbsUp;
    }
  }

  return (
    <>
      {chipTone && (
        <div className={`crm-trust-chip ${chipTone}`}>
          {chipIcon && React.createElement(chipIcon, { size: 13 })}
          {chipLabel}
        </div>
      )}

      {negatives.length > 0 && (
        <div className="crm-alert">
          <AlertTriangle size={13} />
          Último feedback negativo: nota {negatives[0].score} em {formatDate(negatives[0].date)}
        </div>
      )}

      <div className="crm-kpi-row">
        <div className={`crm-kpi-mini enhanced ${!average ? 'empty' : ''}`}
             style={{ '--mini-bg': '#FFFBEB', '--mini-fg': '#D97706' }}>
          <div className="crm-kpi-mini-head">
            <span>Nota média</span>
            <div className="crm-kpi-mini-icon"><Star size={11} /></div>
          </div>
          <strong>{averageDisplay}</strong>
          <div className="crm-kpi-mini-sub">
            {average ? `de ${feedbacks.length} ${feedbacks.length === 1 ? 'feedback' : 'feedbacks'}` : 'sem avaliações'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!feedbacks.length ? 'empty' : ''}`}
             style={{ '--mini-bg': '#EEF2FF', '--mini-fg': '#4F46E5' }}>
          <div className="crm-kpi-mini-head">
            <span>Total de feedbacks</span>
            <div className="crm-kpi-mini-icon"><MessageSquare size={11} /></div>
          </div>
          <strong>{feedbacks.length}</strong>
          <div className="crm-kpi-mini-sub">
            {feedbacks.length
              ? `${positives.length} positivos · ${negatives.length} negativos`
              : 'sem comentários'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!feedbacks[0]?.date ? 'empty' : ''}`}
             style={{ '--mini-bg': '#F0FDFA', '--mini-fg': '#0D9488' }}>
          <div className="crm-kpi-mini-head">
            <span>Último feedback</span>
            <div className="crm-kpi-mini-icon"><Clock size={11} /></div>
          </div>
          {(() => {
            const rel = formatRelativeDate(feedbacks[0]?.date);
            return (
              <>
                <strong>{rel.primary}</strong>
                <div className="crm-kpi-mini-sub">
                  {rel.secondary || 'sem registro'}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {!feedbacks.length ? (
        <EmptyState label="Sem feedbacks recentes." />
      ) : (
        (() => {
          const slice = slicePage(feedbacks);
          return (
            <>
              <div className="crm-list">
                {slice.items.map((feedback, index) => {
                  const tone = feedbackSentimentTone(feedback.class);
                  const score = Number(feedback.score || 0);
                  const hasComment = Boolean(feedback.comment && String(feedback.comment).trim());
                  const label = feedbackSentimentLabel(feedback.class);
                  const isAtendido =
                    feedback.class === 'negativo'
                    && feedback.id != null
                    && feedbacksAtendidosIds.has(String(feedback.id));
                  return (
                    <div
                      className={`crm-row feedback-row stripe-${tone}`}
                      key={`${feedback.id || slice.pageStart + index}-${feedback.date}`}
                    >
                      <span className="crm-reserva-stripe" aria-hidden="true" />
                      <div className="crm-cell left">
                        <div className="crm-reserva-body">
                          {hasComment ? (
                            <div className="crm-feedback-comment">“{feedback.comment}”</div>
                          ) : (
                            <div className="crm-feedback-comment empty">
                              Avaliação sem comentário
                            </div>
                          )}
                          <div className="crm-phone crm-reserva-sub">
                            <span className="crm-reserva-people-inline">
                              <Star size={11} />
                              {score > 0 ? `${score}/5` : 'sem nota'}
                            </span>
                            <span className="crm-reserva-dot" aria-hidden="true">·</span>
                            <span>{formatDate(feedback.date)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="crm-feedback-badges">
                        {isAtendido && (
                          <span className="crm-badge atendido">
                            <CheckCircle2 size={11} />
                            Atendido
                          </span>
                        )}
                        <span className={`crm-badge ${tone}`}>{label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderProfilePagination(slice, feedbacks.length === 1 ? 'feedback' : 'feedbacks')}
            </>
          );
        })()
      )}
    </>
  );
};

const renderReservas = () => {
  const reservas = cliente?.reservas || [];
  const compareceu = reservas.filter((reserva) => reserva.status === 'Compareceu').length;
  const taxa = reservas.length ? Math.round((compareceu / reservas.length) * 100) : 0;
  const grupoMedio = reservas.length
    ? Math.round(reservas.reduce((sum, reserva) => sum + Number(reserva.people || 0), 0) / reservas.length)
    : 0;
  const hasNoShow = reservas.some((reserva) => String(reserva.status || '').toLowerCase().includes('no-show'));
  const trustTone = !reservas.length ? null : hasNoShow ? 'warn' : 'good';

  return (
    <>
      {trustTone && (
        <div className={`crm-trust-chip ${trustTone}`}>
          {trustTone === 'good' ? (
            <>
              <CheckCircle2 size={13} />
              Confiabilidade alta
            </>
          ) : (
            <>
              <AlertTriangle size={13} />
              Confiabilidade requer atenção
            </>
          )}
        </div>
      )}

      <div className="crm-kpi-row">
        <div className={`crm-kpi-mini enhanced ${!reservas.length ? 'empty' : ''}`}
             style={{ '--mini-bg': '#EEF2FF', '--mini-fg': '#4F46E5' }}>
          <div className="crm-kpi-mini-head">
            <span>Total de reservas</span>
            <div className="crm-kpi-mini-icon"><Calendar size={11} /></div>
          </div>
          <strong>{reservas.length}</strong>
          <div className="crm-kpi-mini-sub">
            {reservas.length ? 'no histórico do cliente' : 'sem registros'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!reservas.length ? 'empty' : ''}`}
             style={{ '--mini-bg': '#F0FDFA', '--mini-fg': '#0D9488' }}>
          <div className="crm-kpi-mini-head">
            <span>Taxa de comparecimento</span>
            <div className="crm-kpi-mini-icon"><CheckCircle2 size={11} /></div>
          </div>
          <strong>{taxa}%</strong>
          <div className="crm-kpi-mini-sub">
            {reservas.length ? `${compareceu} de ${reservas.length} reservas` : 'sem base'}
          </div>
        </div>

        <div className={`crm-kpi-mini enhanced ${!grupoMedio ? 'empty' : ''}`}
             style={{ '--mini-bg': '#F5F3FF', '--mini-fg': '#7C3AED' }}>
          <div className="crm-kpi-mini-head">
            <span>Grupo médio</span>
            <div className="crm-kpi-mini-icon"><Users size={11} /></div>
          </div>
          <strong>{grupoMedio || '-'}</strong>
          <div className="crm-kpi-mini-sub">
            {grupoMedio ? 'pessoas por reserva' : 'sem dados'}
          </div>
        </div>
      </div>

      {!reservas.length ? (
        <EmptyState label="Sem reservas registradas." />
      ) : (
        (() => {
          const slice = slicePage(reservas);
          return (
            <>
              <div className="crm-list">
                {slice.items.map((reserva, index) => {
                  const tone = reservationStatusTone(reserva.status);
                  const dateObj = reserva.date ? new Date(reserva.date) : null;
                  const validDate = dateObj && !Number.isNaN(dateObj.getTime());
                  const day = validDate ? String(dateObj.getDate()).padStart(2, '0') : '';
                  const month = validDate ? String(dateObj.getMonth() + 1).padStart(2, '0') : '';
                  const weekday = validDate ? WEEKDAYS_PT[dateObj.getDay()] : '';
                  const rawTime = reserva.time
                    || (validDate
                      ? dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })
                      : '');
                  const timeStr = String(rawTime).slice(0, 5);
                  const people = Number(reserva.people || 0);
                  const dateLabel = validDate ? `${weekday}, ${day}/${month}` : 'Reserva';
                  const titleParts = [dateLabel, timeStr].filter(Boolean).join(' · ');

                  return (
                    <div className={`crm-row reservation-row stripe-${tone}`} key={`${reserva.id || slice.pageStart + index}-${reserva.date}`}>
                      <span className="crm-reserva-stripe" aria-hidden="true" />
                      <div className="crm-cell left">
                        <div className="crm-reserva-body">
                          <div className="crm-name">{titleParts}</div>
                          <div className="crm-phone crm-reserva-sub">
                            <span className="crm-reserva-people-inline">
                              <Users size={11} />
                              {people > 0 ? `${people} ${people === 1 ? 'pessoa' : 'pessoas'}` : 'Sem grupo'}
                            </span>
                            {reserva.origin && (
                              <>
                                <span className="crm-reserva-dot" aria-hidden="true">·</span>
                                <span>via {reserva.origin}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span className={`crm-badge ${tone}`}>{reserva.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderProfilePagination(slice, reservas.length === 1 ? 'reserva' : 'reservas')}
            </>
          );
        })()
      )}
    </>
  );
};

const renderDados = () => {
  const dados = cliente?.dados || {};
  const origemPrincipal = normalizeProductName(dados.origin || cliente?.origin || '');
  const enderecos = dados.addresses || '';
  const addressCount = Number(dados.addressCount || dados.addressList?.length || 0);
  const tipoComum = dados.commonOrder === 'Salao' ? 'Salão' : (dados.commonOrder || '');
  const paymentMethods = Array.isArray(dados.paymentMethods) ? dados.paymentMethods : [];
  const favorito = dados.favorite || '';
  const fontes = uniqueProductNames(Array.isArray(cliente?.origins) ? cliente.origins : []);
  const totalGasto = Number(dados.totalSpent || cliente?.totalSpent || 0);
  const ticketMedio = Number(dados.averageTicket || cliente?.ticket || 0);

  const items = [
    {
      label: 'Origem principal',
      icon: Radio,
      bg: '#EEF2FF',
      fg: '#4F46E5',
      value: origemPrincipal,
    },
    {
      label: 'Endereços',
      icon: Home,
      bg: '#F5F3FF',
      fg: '#7C3AED',
      value: enderecos,
      meta: addressCount > 1 ? `${addressCount} endereços cadastrados` : '',
      action: addressCount > 1 ? onOpenAddresses : null,
      actionLabel: 'Ver todos',
    },
    {
      label: 'Tipo comum',
      icon: ShoppingBag,
      bg: '#F0FDFA',
      fg: '#0D9488',
      value: tipoComum,
    },
    {
      label: 'Valor em pedidos',
      icon: Wallet,
      bg: '#F0FDFA',
      fg: '#0F766E',
      value: totalGasto ? formatMoney(totalGasto) : '',
    },
    {
      label: 'Ticket médio',
      icon: Receipt,
      bg: '#FFFBEB',
      fg: '#B45309',
      value: ticketMedio ? formatMoney(ticketMedio) : '',
    },
    {
      label: 'Métodos de pagamento',
      icon: CreditCard,
      bg: '#FFFBEB',
      fg: '#D97706',
      chips: paymentMethods,
    },
    {
      label: 'Prato favorito',
      icon: Heart,
      bg: '#FFF1F2',
      fg: '#E11D48',
      value: favorito,
    },
    {
      label: 'Produtos vinculados',
      icon: Radio,
      bg: '#EEF2FF',
      fg: '#3730A3',
      chips: fontes,
    },
  ];

  return (
    <div className="crm-data-grid">
      {items.map((item) => {
        const Icon = item.icon;
        const hasChips = Array.isArray(item.chips);
        const isEmpty = hasChips ? item.chips.length === 0 : !item.value;
        const Component = item.action ? 'button' : 'div';
        return (
          <Component
            type={item.action ? 'button' : undefined}
            className={`crm-data-card${item.action ? ' clickable' : ''}`}
            key={item.label}
            onClick={item.action || undefined}
            style={{ '--data-bg': item.bg, '--data-fg': item.fg }}
          >
            <div className="crm-data-card-head">
              <div className="crm-data-card-icon">
                <Icon size={13} />
              </div>
              <span className="crm-data-card-label">{item.label}</span>
            </div>
            {isEmpty ? (
              <div className="crm-data-card-value empty">Não informado</div>
            ) : hasChips ? (
              <div className="crm-data-chips">
                {item.chips.map((chip) => (
                  <span className="crm-data-chip" key={chip}>{chip}</span>
                ))}
              </div>
            ) : (
              <div className="crm-data-card-value">{item.value}</div>
            )}
            {item.meta && <div className="crm-data-card-meta">{item.meta}</div>}
            {item.actionLabel && !isEmpty && (
              <div className="crm-data-card-action">{item.actionLabel}</div>
            )}
          </Component>
        );
      })}
    </div>
  );
};

if (!cliente) return null;
if (activeTab === 'Pedidos') return renderPedidos();
if (activeTab === 'Visitas') return renderVisitas();
if (activeTab === 'Feedback') return renderFeedback();
if (activeTab === 'Reservas') return renderReservas();
return renderDados();
}
